import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, Calendar, Tag, ExternalLink, Clock, 
  Share2, MessageCircle, ArrowRight, Sparkles, AlertTriangle
} from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_LINK = "https://wa.me/33644692901?text=Bonjour Lucie-IA ! J'ai lu votre article et je souhaite en savoir plus sur les formations IA.";

const ArticlePage = () => {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [allNews, setAllNews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchArticle();
  }, [articleId]);

  const fetchArticle = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/ai-news`);
      setAllNews(response.data);
      const found = response.data.find(n => n.id === articleId);
      setArticle(found);
    } catch (error) {
      console.error("Error fetching article:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article?.title,
        text: article?.summary,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const otherNews = allNews.filter(n => n.id !== articleId).slice(0, 3);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#007AFF]/30 border-t-[#007AFF] rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navigation />
        <div className="pt-32 pb-24 text-center">
          <h1 className="text-2xl text-white">Article non trouvé</h1>
          <Link to="/espace-pedagogique" className="text-[#007AFF] mt-4 inline-block">
            ← Retour aux actualités
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="article-page">
      <Navigation />

      <article className="pt-28 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Back link */}
          <Link 
            to="/espace-pedagogique" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'actu de l'IA
          </Link>

          {/* Article Header */}
          <motion.header
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                article.badge === "TENDANCE" ? "bg-purple-500/20 text-purple-400" :
                article.badge === "NOUVEAU" ? "bg-green-500/20 text-green-400" :
                article.badge === "IMPORTANT" ? "bg-red-500/20 text-red-400" :
                "bg-[#007AFF]/20 text-[#007AFF]"
              }`}>
                {article.badge}
              </span>
              <span className="flex items-center gap-1 text-white/50 text-sm">
                <Calendar className="w-4 h-4" />
                {article.date}
              </span>
              <span className="flex items-center gap-1 text-white/50 text-sm">
                <Clock className="w-4 h-4" />
                3 min de lecture
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-4">
              <span className="flex items-center gap-2 text-[#007AFF] text-sm">
                <Tag className="w-4 h-4" />
                {article.relevance}
              </span>
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 text-white/50 hover:text-white text-sm transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Partager
              </button>
            </div>
          </motion.header>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6 sm:p-8 mb-8"
          >
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed mb-6">
              {article.summary}
            </p>

            {article.source && (
              <a 
                href={article.source}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#007AFF] hover:underline mb-6"
              >
                <ExternalLink className="w-4 h-4" />
                Lire la source originale
              </a>
            )}

            <div className="border-t border-white/10 pt-6 mt-6">
              <h3 className="text-lg font-semibold text-white mb-3">
                Ce qu'il faut retenir :
              </h3>
              <ul className="space-y-2 text-white/70">
                <li className="flex items-start gap-2">
                  <span className="text-[#007AFF] mt-1">•</span>
                  L'IA évolue à une vitesse fulgurante - ce qui était impossible hier devient standard aujourd'hui
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#007AFF] mt-1">•</span>
                  Les entreprises qui n'anticipent pas risquent d'être dépassées par leurs concurrents
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#007AFF] mt-1">•</span>
                  Comprendre ces innovations permet d'identifier les opportunités pour votre activité
                </li>
              </ul>
            </div>
          </motion.div>

          {/* CTA Section - Important messaging */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6 sm:p-8 border-[#007AFF]/30 bg-gradient-to-br from-[#007AFF]/10 to-transparent mb-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">
                  Utiliser ChatGPT "un peu" n'est PAS utiliser l'IA
                </h3>
                <p className="text-white/70">
                  La plupart des professionnels pensent "maîtriser l'IA" parce qu'ils ont testé ChatGPT ou Copilot. 
                  En réalité, ils n'exploitent que 5% du potentiel de ces outils.
                </p>
              </div>
            </div>

            <div className="bg-[#0F0F10] rounded-xl p-6 mb-6">
              <p className="text-white text-lg font-medium mb-4 text-center">
                Et vous, quand allez-vous découvrir ce que l'IA permet <span className="text-[#007AFF]">VRAIMENT</span> de faire pour vous aujourd'hui ?
              </p>
              <p className="text-white/60 text-sm text-center mb-6">
                L'Académie IA vous fait découvrir les <strong className="text-white">toutes dernières tendances et outils IA</strong>, 
                et vous permet d'<strong className="text-white">anticiper pour votre entreprise</strong>, 
                plutôt que de risquer d'être submergé demain... ou écrasé par la concurrence !
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/tunnel" 
                  className="btn-primary flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Demander une formation
                </Link>
                <a 
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary flex items-center justify-center gap-2 !bg-green-500/20 !text-green-400 hover:!bg-green-500/30"
                >
                  <MessageCircle className="w-4 h-4" />
                  Discuter avec Lucie-IA
                </a>
              </div>
            </div>
          </motion.div>

          {/* Other News */}
          {otherNews.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h2 className="text-2xl font-bold text-white mb-6">
                Autres actualités IA
              </h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {otherNews.map((news) => (
                  <Link
                    key={news.id}
                    to={`/actualite/${news.id}`}
                    className="glass-card p-4 hover:border-[#007AFF]/30 transition-colors group"
                  >
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      news.badge === "TENDANCE" ? "bg-purple-500/20 text-purple-400" :
                      news.badge === "NOUVEAU" ? "bg-green-500/20 text-green-400" :
                      "bg-[#007AFF]/20 text-[#007AFF]"
                    }`}>
                      {news.badge}
                    </span>
                    <h3 className="text-white font-medium mt-3 mb-2 group-hover:text-[#007AFF] transition-colors line-clamp-2">
                      {news.title}
                    </h3>
                    <p className="text-white/50 text-sm">{news.date}</p>
                  </Link>
                ))}
              </div>
            </motion.section>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default ArticlePage;
